import { useCallback, useEffect, useRef, useState } from 'react';
import CloudDataProvider from './CloudDataProvider';

const assertActive = signal => {
  if (signal.aborted) throw new DOMException('Upload cancelled', 'AbortError');
};

export default function usePresignedUploads() {
  const controller = useRef(null);
  const mounted = useRef(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const cancel = useCallback(() => controller.current?.abort(), []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cancel();
    };
  }, [cancel]);

  const runUploads = useCallback(async uploads => {
    controller.current?.abort();
    const activeController = new AbortController();
    controller.current = activeController;
    const { signal } = activeController;
    if (!mounted.current) activeController.abort();
    const update = () =>
      mounted.current && controller.current === activeController;
    if (update()) {
      setUploading(true);
      setError(null);
      setProgress(null);
    }
    const results = [];
    try {
      assertActive(signal);
      // Each confirmation must finish before initiating the next upload.
      for (let index = 0; index < uploads.length; index += 1) {
        const { file, initiate, confirm } = uploads[index];
        assertActive(signal);
        if (update()) setProgress({ index, fileName: file.name, percent: 0 });
        // eslint-disable-next-line no-await-in-loop
        const { url, headers, uploadId } = await initiate(signal);
        assertActive(signal);
        // eslint-disable-next-line no-await-in-loop
        await CloudDataProvider.uploadUsingPresignedUrl({
          presignedUrl: url,
          file,
          options: {
            headers,
            signal,
            onUploadProgress: event => {
              if (update() && !signal.aborted && event.total) {
                setProgress({
                  index,
                  fileName: file.name,
                  percent: Math.round((event.loaded / event.total) * 100),
                });
              }
            },
          },
        });
        assertActive(signal);
        // eslint-disable-next-line no-await-in-loop
        results.push(await confirm(uploadId, signal));
        assertActive(signal);
      }
      return results;
    } catch (uploadError) {
      assertActive(signal);
      if (update() && !signal.aborted) setError(uploadError);
      throw uploadError;
    } finally {
      if (update()) setUploading(false);
    }
  }, []);

  return { runUploads, cancel, uploading, progress, error };
}
